const MOSCOW_TZ = 'Europe/Moscow';
const DEFAULT_DELIVERY_WINDOW_MINUTES = 120;

/** Текущая дата в Москве YYYY-MM-DD */
export function getTodayMoscow(): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: MOSCOW_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(new Date());
}

/** Завтра в Москве YYYY-MM-DD */
export function getTomorrowMoscow(): string {
  const now = new Date();
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: MOSCOW_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return f.format(tomorrow);
}

/** Текущий час (0–23) в Москве */
export function getCurrentHourMoscow(): number {
  const s = new Date().toLocaleString('en-US', { timeZone: MOSCOW_TZ, hour: '2-digit', hour12: false });
  return parseInt(s, 10) || 0;
}

/**
 * После 21:00 или до 09:00 по Москве — заказы принимаются на следующий день (ближайшая доставка 09:00).
 */
export function getEarliestDeliveryMoscow(): {
  afterHours: boolean;
  earliestDate: string;
  earliestTime: string;
  isNextDay: boolean;
} {
  const hour = getCurrentHourMoscow();
  const today = getTodayMoscow();
  const tomorrow = getTomorrowMoscow();
  if (hour >= 21) {
    return { afterHours: true, earliestDate: tomorrow, earliestTime: '09:00', isNextDay: true };
  }
  if (hour < 9) {
    return { afterHours: true, earliestDate: today, earliestTime: '09:00', isNextDay: false };
  }
  return { afterHours: false, earliestDate: today, earliestTime: '09:00', isNextDay: false };
}

/**
 * Окно доставки в минутах (например +2 часа). Если конец окна попадает на следующий день,
 * в отображении показываем дату следующего дня.
 */

/**
 * Форматирует дату YYYY-MM-DD в DD.MM.YYYY.
 */
function formatDatePart(dateStr: string): string {
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/**
 * Нормализует время до HH:MM (без секунд).
 */
function normalizeTime(timeStr: string): string {
  const trimmed = String(timeStr).trim();
  if (trimmed.length >= 5) return trimmed.slice(0, 5);
  return trimmed;
}

/**
 * Возвращает строку для отображения даты/времени доставки.
 * Если начало + окно доставки (по умолчанию +2 ч) попадает на следующий день,
 * добавляет « – дата_конца время_конца», чтобы была видна дата следующего дня.
 */
export function formatDeliveryDateTime(
  date: string,
  time: string,
  windowMinutes: number = DEFAULT_DELIVERY_WINDOW_MINUTES
): string {
  if (!date?.trim()) return '';
  const timeNorm = normalizeTime(time || '12:00');
  const startStr = `${date.trim()}T${timeNorm}:00`;
  const start = new Date(startStr);
  if (Number.isNaN(start.getTime())) return `${formatDatePart(date)} ${timeNorm}`;

  const end = new Date(start.getTime() + windowMinutes * 60 * 1000);
  const startDateStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
  const endDateStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;

  const displayDate = formatDatePart(date);
  const displayTime = timeNorm;

  if (endDateStr !== startDateStr) {
    const endDisplayDate = formatDatePart(endDateStr);
    const endHours = String(end.getHours()).padStart(2, '0');
    const endMins = String(end.getMinutes()).padStart(2, '0');
    return `${displayDate} ${displayTime} – ${endDisplayDate} ${endHours}:${endMins}`;
  }

  return `${displayDate} ${displayTime}`;
}
